import { defineStore } from 'pinia';
import axios from 'axios';
import { formatDate } from '@/composables/formatDate';
import { isMessageUnread } from '@/composables/messageStatus';
import { useVapCoreFor } from '@/services/backend';
import {
  sendLoginOtp as vapCoreSendLoginOtp,
  verifyLoginOtp as vapCoreVerifyLoginOtp,
} from '@/services/vapcore/auth';
import { saveAttachment as vapCoreSaveAttachment } from '@/services/vapcore/attachments';
import { fetchData as vapCoreFetchData } from '@/services/vapcore/data';
import {
  submitClaim as vapCoreSubmitClaim,
  fetchClaims as vapCoreFetchClaims,
  fetchAdditionalQuestions as vapCoreFetchAdditionalQuestions,
} from '@/services/vapcore/claims';
import {
  fetchMessages as vapCoreFetchMessages,
  markMessageRead as vapCoreMarkMessageRead,
} from '@/services/vapcore/messages';
import * as Sentry from '@sentry/vue';

/** iGard's `find('list')` shape: a plain object mapping id -> display string. */
function lookupItemsToMap(items) {
  return Object.fromEntries((items || []).map((item) => [item.id, item.name]));
}

/**
 * Causal parts additionally carry a group name, shown appended in square brackets — matches
 * ClaimCausalPartsTable::getClaimCausalPartsByPolicyId's valueField closure exactly.
 */
function causalPartItemsToMap(items) {
  return Object.fromEntries(
    (items || []).map((item) => [
      item.id,
      item.description ? `${item.name} [${item.description}]` : item.name,
    ])
  );
}

/** Maps VapCore's camelCase policy shape into the snake_case shape the rest of the app expects. */
function mapVapCorePolicy(policy) {
  return {
    // Stringified to match iGard's own JSON convention (numeric columns come back as strings) —
    // several pages compare this against route params, which Vue Router always gives as strings.
    id: String(policy.id),
    policy_no: policy.policyNo,
    term: policy.term,
    inception_date: policy.inceptionDate,
    expiry_date: policy.expiryDate,
    product: policy.product,
    status: policy.status,
    claims_number_this_year: policy.claimsNumberThisYear,
    claims_annual_limit: policy.claimsAnnualLimit,
    link: policy.link,
    CauseTypes: lookupItemsToMap(policy.causeTypes),
    LossTypes: lookupItemsToMap(policy.lossTypes),
    CausalParts: causalPartItemsToMap(policy.causalParts),
  };
}

function mapVapCoreClaim(claim) {
  return {
    id: String(claim.id),
    // Stringified to match policy.id above — associateClaimsAndPolicies and DashboardPage.vue
    // both match claims to policies with a strict === on these two fields.
    policy_id: String(claim.policyId),
    date_of_loss: claim.dateOfLoss,
    claim_no: claim.claimNo,
    // RequestsPage.vue splits current/historic tabs on this value.
    claim_status: claim.claimStatus,
    claim_causal_part: claim.claimCausalPart,
  };
}

function associateClaimsAndPolicies(claims, policies) {
  claims.sort((dateA, dateB) => {
    return new Date(dateB.date_of_loss) - new Date(dateA.date_of_loss);
  });

  for (let policy of policies) {
    // fetchClaims() calls this a second time with the same this.policies it was already given at
    // login — by then inception_date/expiry_date are already "DD.MM.YYYY" strings from the first
    // pass, and re-parsing that format produces an Invalid Date (NaN.NaN.NaN once reformatted) in
    // most browsers. Only overwrite when the value actually parses as a real date.
    const startDate = new Date(policy.inception_date);
    if (!isNaN(startDate.getTime())) {
      policy.inception_date = formatDate(startDate);
    }

    const expiryDate = new Date(policy.expiry_date);
    if (!isNaN(expiryDate.getTime())) {
      policy.expiry_date = formatDate(expiryDate);
    }
  }

  const claimsWithMatchingPolicy = [];

  for (let claim of claims) {
    const matchingPolicy = policies.find(
      (policy) => policy.id === claim.policy_id
    );

    // Policies and Claims come from two separate queries against a live database (dual-path, so
    // this now runs against VapCore's response too, not just iGard's) — a claim whose policy
    // isn't in this same response (e.g. cancelled between the two queries) is dropped rather than
    // throwing and aborting the whole customer-data load right after a successful login.
    if (!matchingPolicy) {
      Sentry.captureMessage(
        `associateClaimsAndPolicies: claim ${claim.id} has no matching policy ${claim.policy_id}`
      );
      continue;
    }

    const dateOfLoss = new Date(claim.date_of_loss);

    claim.product = matchingPolicy.product;
    claim.policy_no = matchingPolicy.policy_no;
    claim.policy_term = matchingPolicy.term;
    claim.policy_inception_date = matchingPolicy.inception_date;
    claim.policy_expiry_date = matchingPolicy.expiry_date;
    claim.claims_number_this_year = matchingPolicy.claims_number_this_year;
    claim.claims_annual_limit = matchingPolicy.claims_annual_limit;
    claim.policy_status = matchingPolicy.status;
    claim.date_of_loss = formatDate(dateOfLoss);
    claim.link = matchingPolicy.link;

    claimsWithMatchingPolicy.push(claim);
  }

  return { claims: claimsWithMatchingPolicy, policies };
}

export const useCustomerStore = defineStore('customer', {
  state: () => ({
    isAuthorised: false,
    token: '',
    customer: [],
    vehicle: [],
    policies: [],
    claims: [],
    // Care Portal messages, keyed by claim id - fetched per claim rather than all at once, since
    // a customer only opens the Messages screen for one claim at a time.
    messagesByClaimId: {},
    mileage: '',
    submittedPhotos: [],
    claimantEmail: '',
    dateOfLoss: '',
    countryIncident: '',
    claimLossTypeId: '',
    claimCauseTypeId: '',
    claimCausalPartId: '',
    causeNotes: '',
    additionalQuestions: [],
    questionsAnswers: {},
    // Held between the login and verify steps so verify can bind its request to the same
    // identity the OTP was issued for (VapCore's verify endpoint requires this — see
    // _fetchCustomerFromVapCore). Not needed by the iGard path, which only ever needed the code.
    loginSurname: '',
    loginRegNo: '',
  }),
  getters: {
    getCustomer(state) {
      return state.customer;
    },
    hasUnreadMessages(state) {
      return Object.values(state.messagesByClaimId).some((messages) =>
        messages.some(isMessageUnread)
      );
    },
  },
  actions: {
    /**
     * Requests a login OTP for an existing policyholder, from whichever backend is configured to
     * serve auth. Both paths return the same { success, code } shape LoginForm.vue reads, and
     * throw an error carrying a normalized `.reason` (`not_found|cooldown|send_failed`) the
     * component can branch its messaging on without knowing which backend answered. VapCore masks
     * "customer has no email/phone on file" as `not_found` too, deliberately — otherwise a valid
     * surname/reg with no contact method on file would be distinguishable from an unknown one,
     * letting an anonymous caller enumerate real customer identities.
     */
    async sendLoginOtp(surname, regNo) {
      this.loginSurname = surname;
      this.loginRegNo = regNo;

      return useVapCoreFor('auth')
        ? this._sendLoginOtpFromVapCore(surname, regNo)
        : this._sendLoginOtpFromIGard(surname, regNo);
    },

    async _sendLoginOtpFromVapCore(surname, regNo) {
      try {
        const { sent, code } = await vapCoreSendLoginOtp(surname, regNo);
        return { success: sent, code };
      } catch (error) {
        error.reason = error.response?.data?.reason || 'not_found';
        throw error;
      }
    },

    /** iGard's failure reasons come back as a message string rather than a typed reason. */
    async _sendLoginOtpFromIGard(surname, regNo) {
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/signIn`;
        const response = await axios.post(url, { surname, reg_no: regNo });

        return { success: response.data.success, code: response.data.code };
      } catch (error) {
        const message = error.response?.data?.error?.message || '';
        error.reason = message.includes('Could not sent verification code')
          ? 'send_failed'
          : 'not_found';
        throw error;
      }
    },

    /**
     * Redeems a login OTP and loads the customer's data, from whichever backend is configured to
     * serve auth. On success both paths populate the same state and return the same
     * {data:{success}} shape VerifyForm.vue reads. On failure the VapCore path throws a
     * normalized `.reason` (`expired|invalid|rate_limited`) plus `.attemptsRemaining` for
     * VerifyForm.vue to show; the iGard path still swallows its error and resolves to undefined
     * instead (a separately-tracked bug, not fixed here).
     */
    async fetchCustomer(passcode) {
      return useVapCoreFor('auth')
        ? this._fetchCustomerFromVapCore(passcode)
        : this._fetchCustomerFromIGard(passcode);
    },

    async _fetchCustomerFromIGard(passcode) {
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/signInWithCode`;
        const data = { code: passcode };
        const response = await axios.post(url, data);

        if (response.data.success) {
          this.isAuthorised = true;
        }

        this.token = response.data.token;
        this.customer = response.data.data.Customer;
        this.vehicle = response.data.data.Vehicle;
        const { claims, policies } = associateClaimsAndPolicies(
          response.data.data.Claims,
          response.data.data.Policies
        );
        this.claims = claims;
        this.policies = policies;

        return response;
      } catch (error) {
        if (error.response) {
          Sentry.captureException(error.response.data.error);
          Sentry.captureMessage(error.response.data.error.message);
        } else {
          Sentry.captureException(error);
          Sentry.captureMessage(error.message);
        }
      }
    },

    /**
     * VapCore only verifies the OTP and issues a session token — the same token it bridges into
     * iGard's own mobile_auths table, so the still-iGard /getData call this delegates to (when
     * `policies` isn't yet enabled) is accepted there exactly as it would be for an iGard-issued
     * token.
     *
     * The verify call and the data-fetch call are deliberately in separate try/catch blocks: they
     * fail for entirely different reasons (wrong/expired code vs. a network or backend problem
     * fetching the customer bundle), and collapsing both into one handler previously mislabeled
     * every data-fetch failure as "Incorrect code" — genuinely confusing when the code was right
     * and login had already succeeded.
     */
    async _fetchCustomerFromVapCore(passcode) {
      let token;
      try {
        ({ token } = await vapCoreVerifyLoginOtp(
          this.loginSurname,
          this.loginRegNo,
          passcode
        ));
      } catch (error) {
        // Re-thrown (not swallowed) with a normalized `.reason`/`.attemptsRemaining` so
        // VerifyForm.vue can show something meaningful — swallowing this and returning undefined
        // left `customer.data.success` throwing "Cannot read properties of undefined" for any
        // verify failure (expired code, wrong code, rate limited), masking the real reason behind
        // a JS error instead of a message the customer could act on.
        error.reason = error.response?.data?.reason || 'invalid';
        error.attemptsRemaining = error.response?.data?.attemptsRemaining;
        Sentry.captureException(error);
        throw error;
      }

      this.token = token;
      this.isAuthorised = true;

      try {
        return useVapCoreFor('policies')
          ? await this._fetchCustomerDataFromVapCore()
          : await this._fetchCustomerDataFromIGard();
      } catch (error) {
        // Login already succeeded at this point — don't let VerifyForm.vue's invalid/expired
        // messaging apply to what is really a data-fetch failure. A distinct reason is required
        // here, not just leaving `.reason` unset: VerifyForm.vue's fallback chain is
        // `messagesByReason[error.reason] || error.message || 'Something went wrong...'`, and
        // axios errors always populate `.message` (e.g. "Request failed with status code 500"),
        // so that clause wins over the intended generic message unless `.reason` maps to
        // something explicitly.
        error.reason = 'data_fetch_failed';
        Sentry.captureException(error);
        throw error;
      }
    },

    async _fetchCustomerDataFromIGard() {
      const url = `${process.env.VUE_APP_API_BASE_URL}/getData`;
      const response = await axios.get(url, {
        headers: { 'X-Auth-Token': this.token },
      });

      this.customer = response.data.data.Customer;
      this.vehicle = response.data.data.Vehicle;
      const { claims, policies } = associateClaimsAndPolicies(
        response.data.data.Claims,
        response.data.data.Policies
      );
      this.claims = claims;
      this.policies = policies;

      return response;
    },

    /**
     * Maps VapCore's own camelCase response into the exact snake_case shape
     * associateClaimsAndPolicies and the rest of the app already consume, and wraps the result to
     * match the `{ data: { success } }` shape VerifyForm.vue reads regardless of which backend
     * answered.
     */
    async _fetchCustomerDataFromVapCore() {
      const data = await vapCoreFetchData();

      this.customer = data.customer
        ? [
            {
              first_name: data.customer.firstName,
              last_name: data.customer.lastName,
              address1: data.customer.address1,
              address2: data.customer.address2,
              city: data.customer.city,
              postal_code: data.customer.postalCode,
              email: data.customer.email,
              cell_phone: data.customer.cellPhone,
              telephone: data.customer.telephone,
            },
          ]
        : [];

      this.vehicle = data.vehicle
        ? [
            {
              make: data.vehicle.make,
              model: data.vehicle.model,
              vrm: data.vehicle.vrm,
              vin: data.vehicle.vin,
            },
          ]
        : [];

      this._mergeAndSetClaims(
        data.claims || [],
        (data.policies || []).map(mapVapCorePolicy)
      );

      return { data: { success: true } };
    },
    /**
     * Maps VapCore claims into the shape associateClaimsAndPolicies expects, merges them against
     * the given policies, and assigns the result — shared by _fetchCustomerDataFromVapCore and
     * _fetchClaimsFromVapCore so the merge/assignment logic can't drift between the two call sites.
     */
    _mergeAndSetClaims(vapCoreClaims, policies) {
      const { claims, policies: mergedPolicies } = associateClaimsAndPolicies(
        vapCoreClaims.map(mapVapCoreClaim),
        policies
      );
      this.claims = claims;
      this.policies = mergedPolicies;
    },
    async fetchAdditionalQuestions(claimLossTypeId) {
      return useVapCoreFor('questions')
        ? this._fetchAdditionalQuestionsFromVapCore(claimLossTypeId)
        : this._fetchAdditionalQuestionsFromIGard(claimLossTypeId);
    },
    async _fetchAdditionalQuestionsFromIGard(claimLossTypeId) {
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/getClaimAdditionalQuestions?claimLossTypeId=${claimLossTypeId}`;
        const response = await axios.get(url, {
          headers: { 'X-Auth-Token': this.token },
        });

        this.additionalQuestions = response.data.data;

        return response;
      } catch (error) {
        if (error.response) {
          Sentry.captureException(error.response.data.error);
          Sentry.captureMessage(error.response.data.error.message);
        } else {
          Sentry.captureException(error);
          Sentry.captureMessage(error.message);
        }
      }
    },
    async _fetchAdditionalQuestionsFromVapCore(claimLossTypeId) {
      try {
        const questions = await vapCoreFetchAdditionalQuestions(claimLossTypeId);
        this.additionalQuestions = questions;
        // RequestDetailsPage.vue checks response.data.success before navigating on — matches
        // the {data:{success:true}} contract already established for other dual-path actions.
        return { data: { success: true } };
      } catch (error) {
        Sentry.captureException(error);
        Sentry.captureMessage(
          `${error.response?.data?.reason || error.message}, ClaimLossTypeId: ${claimLossTypeId}`
        );
      }
    },
    async fetchClaims() {
      return useVapCoreFor('claims')
        ? this._fetchClaimsFromVapCore()
        : this._fetchClaimsFromIGard();
    },
    async _fetchClaimsFromIGard() {
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/getClaims`;
        const response = await axios.get(url, {
          headers: { 'X-Auth-Token': this.token },
        });

        const { claims, policies } = associateClaimsAndPolicies(
          response.data.data,
          this.policies
        );
        this.claims = claims;
        this.policies = policies;
      } catch (error) {
        if (error.response) {
          Sentry.captureException(error.response.data.error);
          Sentry.captureMessage(error.response.data.error.message);
        } else {
          Sentry.captureException(error);
          Sentry.captureMessage(error.message);
        }

        Sentry.captureException(error);
      }
    },
    async _fetchClaimsFromVapCore() {
      try {
        const vapCoreClaims = await vapCoreFetchClaims();
        this._mergeAndSetClaims(vapCoreClaims, this.policies);
      } catch (error) {
        Sentry.captureException(error);
        Sentry.captureMessage(error.response?.data?.reason || error.message);
      }
    },
    /**
     * Care Portal messages are new - there is no iGard equivalent to fall back to, so unlike
     * fetchClaims this always calls VapCore directly rather than branching on useVapCoreFor.
     *
     * Returns whether the fetch succeeded, so callers can distinguish "still loading" from
     * "fetch failed" from "loaded, zero messages" instead of a failure looking identical to an
     * empty inbox forever (this used to leave messagesByClaimId[claimId] unset on any error,
     * which pages read as "still loading" with no way out).
     */
    async fetchMessagesForClaim(claimId) {
      try {
        const messages = await vapCoreFetchMessages(claimId);
        this.messagesByClaimId = { ...this.messagesByClaimId, [claimId]: messages };
        return true;
      } catch (error) {
        Sentry.captureException(error);
        Sentry.captureMessage(error.response?.data?.reason || error.message);
        return false;
      }
    },
    async markMessageRead(claimId, messageId) {
      try {
        await vapCoreMarkMessageRead(claimId, messageId);
        const messages = this.messagesByClaimId[claimId] || [];
        this.messagesByClaimId = {
          ...this.messagesByClaimId,
          [claimId]: messages.map((message) =>
            message.id === messageId ? { ...message, readAt: new Date().toISOString() } : message
          ),
        };
      } catch (error) {
        Sentry.captureException(error);
        Sentry.captureMessage(error.response?.data?.reason || error.message);
      }
    },
    /**
     * Submits a new claim against a policy, from whichever backend is configured to serve
     * submissions. Both paths swallow their own errors and resolve to undefined on failure
     * (an existing, established contract — AdditionalQuestionsPage.vue checks
     * `response === undefined` to show its error state), and both return a `{data:{success}}`
     * shape on success.
     */
    async submitClaim(policyId) {
      return useVapCoreFor('submit')
        ? this._submitClaimFromVapCore(policyId)
        : this._submitClaimFromIGard(policyId);
    },

    async _submitClaimFromIGard(policyId) {
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/saveClaim`;
        const data = {
          policy_id: policyId,
          claimant_email: this.claimantEmail || this.customer[0].email,
          date_of_loss: new Date(this.dateOfLoss).toISOString().slice(0, 10),
          claim_loss_type_id: this.claimLossTypeId,
          claim_cause_type_id: this.claimCauseTypeId,
          claim_causal_part_id: this.claimCausalPartId,
          mileage_on_dol: this.mileage,
          cause_notes: this.causeNotes,
          token: this.token,
          questions_answers: Object.entries(this.questionsAnswers).map(
            ([claim_question_id, answer]) => ({
              claim_question_id,
              answer,
            })
          ),
        };
        const response = await axios.post(url, data, {
          headers: { 'X-Auth-Token': this.token },
        });
        const newClaimId = response.data.data.Claims[0].id;

        for (const photo of this.submittedPhotos) {
          await this.saveAttachment(photo, newClaimId);
        }

        await this.resetNewRequest();

        return response;
      } catch (error) {
        if (error.response) {
          Sentry.captureException(error.response.data.error);
          Sentry.captureMessage(
            `${error.response.data.error.message}, PolicyID: ${policyId}`
          );
        } else {
          Sentry.captureException(error);
          Sentry.captureMessage(`${error.message}, PolicyID: ${policyId}`);
        }
      }
    },

    async _submitClaimFromVapCore(policyId) {
      try {
        const { claimId } = await vapCoreSubmitClaim({
          policyId,
          dateOfLoss: new Date(this.dateOfLoss).toISOString().slice(0, 10),
          claimLossTypeId: this.claimLossTypeId,
          claimCauseTypeId: this.claimCauseTypeId,
          claimCausalPartId: this.claimCausalPartId,
          claimantEmail: this.claimantEmail || this.customer[0].email,
          mileageOnDol: this.mileage,
          causeNotes: this.causeNotes,
          questionsAnswers: Object.entries(this.questionsAnswers).map(
            ([claimQuestionId, answer]) => ({
              claimQuestionId: Number(claimQuestionId),
              answer,
            })
          ),
        });

        for (const photo of this.submittedPhotos) {
          await this.saveAttachment(photo, claimId);
        }

        await this.resetNewRequest();

        return { data: { success: true } };
      } catch (error) {
        Sentry.captureException(error);
        Sentry.captureMessage(
          `${error.response?.data?.reason || error.message}, PolicyID: ${policyId}`
        );
      }
    },
    /**
     * Uploads a claim photo, from whichever backend is configured to serve documents. Unlike the
     * iGard path (which swallows its own errors below, an existing, separately-tracked issue not
     * fixed here), the VapCore path re-throws so callers' existing try/catch blocks (e.g.
     * AdditionalPhotoUploadModalComponent's per-photo failure toast) actually fire.
     */
    async saveAttachment(photo, newClaimId) {
      return useVapCoreFor('documents')
        ? this._saveAttachmentFromVapCore(photo, newClaimId)
        : this._saveAttachmentFromIGard(photo, newClaimId);
    },

    async _saveAttachmentFromVapCore(photo, newClaimId) {
      return await vapCoreSaveAttachment(newClaimId, photo.fileName, photo.data);
    },

    async _saveAttachmentFromIGard(photo, newClaimId) {
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/saveAttachment`;
        const data = {
          token: this.token,
          data: photo.data,
          claim_id: newClaimId,
          filename: photo.fileName,
        };

        return await axios.post(url, data, {
          headers: { 'X-Auth-Token': this.token },
        });
      } catch (error) {
        if (error.response) {
          Sentry.captureException(error.response.data.error);
          Sentry.captureMessage(
            `${error.response.data.error.message}, ClaimID: ${newClaimId}, FileName: ${photo.fileName}`
          );
        } else {
          Sentry.captureException(error);
          Sentry.captureMessage(
            `${error.message}, ClaimID: ${newClaimId}, FileName: ${photo.fileName}`
          );
        }
      }
    },
    async resetNewRequest() {
      this.mileage = '';
      this.submittedPhotos = [];
      this.dateOfLoss = '';
      this.claimLossTypeId = '';
      this.claimCauseTypeId = '';
      this.claimCausalPartId = '';
      this.causeNotes = '';
      this.additionalQuestions = [];
      this.questionsAnswers = {};
    },
  },
  persist: true,
});
